import { Dialog } from '@headlessui/react';
import { Download, FileText, X } from 'lucide-react';
import { downloadFile, filenameFromUrl } from '../lib/utils';

type PreviewKind = 'pdf' | 'image' | 'unsupported';

interface PreviewModalProps {
  url: string | null;
  title?: string;
  kind?: PreviewKind;
  filename?: string;
  onClose: () => void;
}

function getPreviewKind(url: string): PreviewKind {
  const path = url.split('?')[0].toLowerCase();
  if (path.endsWith('.pdf')) return 'pdf';
  if (/\.(png|jpe?g|gif|webp|svg)$/.test(path)) return 'image';
  if (/\.(xlsx?|xls|csv)$/.test(path)) return 'unsupported';
  // No extension (e.g. Unsplash) — treat as image
  return 'image';
}

export default function PreviewModal({ url, title = 'Preview', kind: kindOverride, filename, onClose }: PreviewModalProps) {
  const kind = kindOverride ?? (url ? getPreviewKind(url) : 'unsupported');

  const handleDownload = () => {
    if (!url) return;
    downloadFile(url, filename ?? filenameFromUrl(url, 'file'));
  };

  return (
    <Dialog open={!!url} onClose={onClose} className="relative z-[99999]">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-[90vw] max-w-5xl h-[90vh] flex flex-col bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
          <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <Dialog.Title className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <FileText className="h-4 w-4 text-gray-400" />
              {title}
            </Dialog.Title>
            <div className="flex items-center gap-2">
              {url && (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-3 py-1.5 rounded-lg text-sm flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-500 font-semibold"
                >
                  <Download className="w-4 h-4" /> Descargar
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="bg-red-50 rounded-lg p-2 hover:bg-red-100 transition-colors duration-300"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-red-500" />
              </button>
            </div>
          </div>

          {url && kind === 'pdf' && (
            <iframe
              src={`${url}#toolbar=0&navpanes=0`}
              className="flex-1 w-full border-0"
              title={title}
            />
          )}

          {url && kind === 'image' && (
            <div className="flex-1 flex items-center justify-center overflow-auto bg-gray-50 p-4">
              <img src={url} alt={title} className="max-w-full max-h-full object-contain" />
            </div>
          )}

          {url && kind === 'unsupported' && (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6">
              <p className="text-gray-800 font-medium">Archivo no compatible</p>
              <p className="text-sm text-gray-500">Este tipo de archivo no se puede previsualizar. Descárguelo.</p>
            </div>
          )}
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
